import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Mail, Globe, Shield, RefreshCw, Key, CheckCircle, AlertTriangle, Play, Check, Copy, HelpCircle, ArrowRight,
  Database, Eye, Sparkles, Send, FileText, ChevronRight, Server, Plus, Trash2, Sliders, Inbox, Archive, UserCheck,
  ChevronDown, MessageSquare, Briefcase, Zap, Flame, Clock, BadgeAlert, AlertCircle, Cpu
} from 'lucide-react';
import { NexusUser, WorkSuiteTask } from '../types/nexus';

interface NexusMailDomainsProps {
  user: NexusUser;
  isAdminMode: boolean;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  onAddTaskToWorkSuite: (title: string, priority: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW') => void;
  searchCredits: number;
  onAddSearchCredits: (amount: number, cost: number) => void;
}

export const NexusMailDomains: React.FC<NexusMailDomainsProps> = ({
  user,
  isAdminMode,
  onAddLog,
  onAddTaskToWorkSuite,
  searchCredits,
  onAddSearchCredits
}) => {
  const [activeTab, setActiveTab] = useState<'Overview' | 'Domains' | 'Mailboxes' | 'AuthCenter' | 'DNS' | 'Smtp' | 'Inbox' | 'Templates' | 'Wizard'>('Overview');

  // --- STATE 1: Connected Domains ---
  const [domains, setDomains] = useState([
    { id: 'dom-1', name: 'nexoraxdigital.co.uk', status: 'Connected', registrar: 'Registrar API Required', dnsStatus: 'Connected', sslStatus: 'SSL Active', hostingConnected: 'Hetzner Dedicated PX93', emailEnabled: true, renewalDate: '2027-06-12' },
    { id: 'dom-2', name: 'galaxyquantum.app', status: 'Pending DNS', registrar: 'Request Domain Setup', dnsStatus: 'Pending DNS', sslStatus: 'SSL Pending', hostingConnected: 'None', emailEnabled: false, renewalDate: '2027-01-20' },
    { id: 'dom-3', name: 'mahmoodeffect.com', status: 'Verification Required', registrar: 'Domain Registration Available Soon', dnsStatus: 'Verification Required', sslStatus: 'SSL Checked', hostingConnected: 'None', emailEnabled: false, renewalDate: '2026-11-05' }
  ]);
  const [domainSearchQuery, setDomainSearchQuery] = useState('');
  const [domainSearchOutput, setDomainSearchOutput] = useState<{ name: string; available: boolean; price?: number } | null>(null);
  const [lastCopiedText, setLastCopiedText] = useState<string | null>(null);

  // --- STATE 2: Business Mailboxes ---
  const [mailboxes, setMailboxes] = useState([
    { id: 'mail-1', email: 'support@nexoraxdigital.co.uk', type: 'Support inbox', status: 'Active', storage: '2.4 GB / 50 GB', forwarding: false, smtp: true, lastActivity: '2 mins ago' },
    { id: 'mail-2', email: 'info@nexoraxdigital.co.uk', type: 'Business mailbox', status: 'Active', storage: '0.8 GB / 50 GB', forwarding: false, smtp: true, lastActivity: '15 mins ago' },
    { id: 'mail-3', email: 'billing@nexoraxdigital.co.uk', type: 'Billing inbox', status: 'SMTP Ready', storage: '120 MB / 10 GB', forwarding: true, smtp: true, lastActivity: '1 day ago' },
    { id: 'mail-4', email: 'sales@nexoraxdigital.co.uk', type: 'Sales inbox', status: 'Active', storage: '0 GB / 20 GB', forwarding: false, smtp: false, lastActivity: 'Never' },
    { id: 'mail-5', email: 'post@galaxyquantum.app', type: 'Forwarding address', status: 'Forwarding Only', storage: '0 GB', forwarding: true, smtp: false, lastActivity: '3 mins ago' }
  ]);
  const [newMailUser, setNewMailUser] = useState('');
  const [newMailDomain, setNewMailDomain] = useState('nexoraxdigital.co.uk');
  const [newMailType, setNewMailType] = useState('Business mailbox');

  // --- STATE 3: Auth Center Checklist ---
  const [authChecklist, setAuthChecklist] = useState([
    { id: 'auth-1', name: 'Domain sending verified', status: 'Verified', details: 'DKIM and SPF records parsed correctly by secure gateway' },
    { id: 'auth-2', name: 'SPF record configured (v=spf1 include:nexorax.co.uk ~all)', status: 'Verified', details: 'No IP collisions detected on global relay checkers' },
    { id: 'auth-3', name: 'DKIM records configured (nx_domain_key._domainkey)', status: 'Verified', details: 'RSA-2048 high-fidelity cryptographic verified key' },
    { id: 'auth-4', name: 'DMARC record configured (p=quarantine; pct=100;)', status: 'Pending DNS', details: 'Global DNS caches may take up to 24 hours to align' },
    { id: 'auth-5', name: 'Return-path / bounce handling target matched', status: 'Recommended', details: 'Configure dedicated return envelope to route bounces to bounce@nexorax' },
    { id: 'auth-6', name: 'From address verified configuration', status: 'Verified', details: 'Secure server header matches outgoing SMTP handshake' },
    { id: 'auth-7', name: 'Unsubscribe header matched for marketing reels', status: 'Needs Review', details: 'Requires standard RFC-2369 unsubscribe headers' },
    { id: 'auth-8', name: 'Deliverability test completed checks', status: 'Admin Setup Required', details: 'Trigger a raw delivery sequence report' }
  ]);

  // --- STATE 4: DNS Records ---
  const [dnsRecords, setDnsRecords] = useState([
    { id: 'dns-1', type: 'MX', host: '@', value: '10 relay.nexoraxdigital.co.uk.', status: 'Verified', requiredFor: 'Inbound Ingress Mail Routing' },
    { id: 'dns-2', type: 'TXT', host: '@', value: 'v=spf1 include:nexorax.co.uk ~all', status: 'Verified', requiredFor: 'Sender Policy Framework Approval' },
    { id: 'dns-3', type: 'TXT', host: 'nx_key._domainkey', value: 'v=DKIM1; k=rsa; p=MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA0B6...', status: 'Pending DNS', requiredFor: 'Cryptographic SPF/Sender Signature' },
    { id: 'dns-4', type: 'TXT', host: '_dmarc', value: 'v=DMARC1; p=quarantine; ruf=mailto:dmarc@nexoraxdigital.co.uk;', status: 'Pending DNS', requiredFor: 'Anti-Spoofing Alignment Filter' },
    { id: 'dns-5', type: 'A', host: '@', value: '46.4.105.188', status: 'Verified', requiredFor: 'Hetzner Container Dedicated IP' },
    { id: 'dns-6', type: 'CNAME', host: 'www', value: 'nexoraxdigital.co.uk.', status: 'Verified', requiredFor: 'CNAME Wildcard Subdomain Routing' },
    { id: 'dns-7', type: 'TXT', host: '@', value: 'nx-verification-site-token=7cf32-8491a-f12b0', status: 'Verified', requiredFor: 'General Ownership Asset Verification' }
  ]);

  // --- STATE 5: Nexus Mailbox (Simple Inbox) ---
  const [inboxEmails, setInboxEmails] = useState([
    { id: 'msg-1', sender: 'eleanor.v1@nexorax.co.uk', subject: 'Inquiry regarding Secure Invoice Register Interview coordination', date: '04:12 PM', body: 'Greetings,\n\nI checked the NexoraX billing records mandate and NH448796B reference. We are eager to progress my application. Can the Director call me on my verified number at 07359062464 or initiate a virtual portal interview?', status: 'New', category: 'Support inbox', urgency: 'High', aiSummary: 'Eleanor is requesting an immediate follow-up interview for the Secure Invoice Register role. Reference NH448796B NexoraX billing records.' },
    { id: 'msg-2', sender: 'info@hetzner-baremetal.de', subject: 'Server base container telemetry node stable', date: '01:45 PM', body: 'Dear Customer,\n\nYour German dedicated server PX93 has fully synchronised under 0.8s limits. Incoming shunting logs have zero anomalies.', status: 'Resolved', category: 'Domain setup messages', urgency: 'Low', aiSummary: 'Hetzner confirms PX93 server telemetry baseline is solid and shunting logs have cleared.' },
    { id: 'msg-3', sender: 'claire.executive@Secure Payment Provider.gov.uk', subject: 'NexoraX billing records Grant Authorization Protocol', date: '11:30 AM', body: 'To Lead Director,\n\nWe have parsed the £150,000 resource specifications. We require an authorized signature for the direct billing placement forms to connect your remote nodes.', status: 'In Progress', category: 'Billing enquiries', urgency: 'Urgent', aiSummary: 'Secure Payment Provider requires an authorized signature to release Direct Billing support for remote nodes.' },
    { id: 'msg-4', sender: 'sales-leads@globalcompanies.org', subject: 'Corporate B2B cloud integration enquiry', date: 'Yesterday', body: 'Hi Office of Lead Director,\n\nWe saw the NexoraX Multiverse Search and need to connect our core CRM to German Hetzner containers. Is there an enterprise package with 20% Tax Ledger reporting?', status: 'Awaiting Reply', category: 'Sales enquiries', urgency: 'Medium', aiSummary: 'Incoming B2B prospect seeks integration with German Hetzner containers and custom Tax auditing.' }
  ]);
  const [selectedMail, setSelectedMail] = useState<any>(inboxEmails[0]);
  const [aiDraftMessage, setAiDraftMessage] = useState('');
  const [isDraftLoading, setIsDraftLoading] = useState(false);

  // --- STATE 6: SMTP Settings ---
  const [smtpStatusInfo, setSmtpStatusInfo] = useState({
    providerConnected: 'NexoraX MailRelay Edge Network (Hetzner Port 3000 Secured)',
    domainVerified: 'Yes (nexoraxdigital.co.uk)',
    smtpSendingEnabled: true,
    dailySendingLimit: '50,000 / 50,000 queries',
    monthlySendingLimit: '1,500,000 remaining',
    lastTestStatus: 'Success (June 14, 2026, 03:15 UTC)',
    bounceHandlingStatus: 'Dedicated Bounce Envelope Enabled'
  });

  // --- STATE 7: Template Library ---
  const [templates, setTemplates] = useState([
    { id: 'tpl-1', name: 'Welcome Onboard Matrix', category: 'Welcome email', subject: 'NexoraX Nexus Workspace Aligned: [BUSINESS_NAME]', body: 'Greetings [CLIENT_NAME],\n\nWelcome to your dedicated workspace: [WEBSITE_URL]. Your secure email handles are active and mapped directly to the H100 cluster priority logs.\n\nWarm regards,\nOffice of the Director,\nNexoraX Digital Ltd', approvalRequired: false },
    { id: 'tpl-2', name: 'Secure Payment Provider Secure Invoice Register Support Response', category: 'Support reply', subject: 'URGENT CO-ORDINATION: NexoraX billing records Secure Invoice Register Placement ([CLIENT_NAME])', body: 'Submitting secure update to Executive Support,\n\nYour placement has been flagged under NI: NH448796B. We require immediate coordination. Please establish communications with Lead Director on the freephone frequency 08000 622827 or clear terminal queues.\n\nRegards,\nNexoraX Chief of Staff', approvalRequired: true },
    { id: 'tpl-3', name: 'Payment Ledger Invoice Release', category: 'Invoice reminder', subject: 'INVOICE MANDATE: Fiscal Settlement for [PLAN_NAME]', body: 'Secure Billing Dispatch:\n\nYour raw processing ledger has generated invoice: [SUPPORT_LINK]. Please clear upfront activation deposit to ensure no interruption on German container shunting systems.\n\nSort Code: Secure Payment Provider (04-06-05) • Account: 31396515\n\nDirect support via info@nexoraxdigital.co.uk', approvalRequired: true },
    { id: 'tpl-4', name: 'B2B Sales Outreach Reel', category: 'Sales follow-up', subject: 'REVENUE OPTIMIZATION: Connect your domain [WEBSITE_URL] to H100 GPU core', body: 'Dear [CLIENT_NAME],\n\nWe scanned your landscape profile via Multiverse indices. We have a dedicated onboarding proxy configured to upgrade your digital footprint. Secure your slot via payment link: [BOOKING_LINK].\n\nRegards,\nVanguard-Closer Node-01', approvalRequired: false }
  ]);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(templates[0]);
  const [previewTemplateReplaced, setPreviewTemplateReplaced] = useState('');

  // --- STATE 8: Interactive Wizard Steps ---
  const [currentWizardStep, setCurrentWizardStep] = useState(1);
  const [wizardDomainInput, setWizardDomainInput] = useState('newventure-nexus.co.uk');
  const [wizardSmtpTestSent, setWizardSmtpTestSent] = useState(false);
  const [wizardChecklist, setWizardChecklist] = useState({
    step1: true,
    step2: false,
    step3: false,
    step4: false,
    step5: false,
    step6: false,
    step7: false,
    step8: false
  });

  // --- COPY HELPER ---
  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setLastCopiedText(id);
    onAddLog('NexoraX Mail & Domains', 'Copied DNS value/template payload', 'SUCCESS', `Snippet copied. Text snippet: ${text.substring(0, 25)}...`);
    setTimeout(() => setLastCopiedText(null), 1500);
  };

  // --- INTERACTIVE ACTIONS ---

  // Check Domain Availability
  const handleCheckDomain = () => {
    if (!domainSearchQuery.trim()) return;
    const isAvailable = !['google.com', 'apple.com', 'nexorax.co.uk', 'facebook.com', 'microsoft.com'].includes(domainSearchQuery.toLowerCase());
    const price = isAvailable ? (domainSearchQuery.endsWith('.co.uk') ? 7.99 : domainSearchQuery.endsWith('.app') ? 24.99 : 12.99) : undefined;
    setDomainSearchOutput({
      name: domainSearchQuery.toLowerCase(),
      available: isAvailable,
      price
    });
    onAddLog('NexoraX Domains', 'Domain availability query', 'SUCCESS', `Searched domain: ${domainSearchQuery}. Response: ${isAvailable ? 'Available' : 'Taken'}`);
  };

  // Request Domain Connection (Or Placeholder Register)
  const handleConnectDomain = (name: string) => {
    if (domains.find(d => d.name === name)) {
      alert('This domain is already cataloged inside our systems.');
      return;
    }
    const newDom = {
      id: `dom-${Date.now()}`,
      name,
      status: 'Pending DNS',
      registrar: 'Request Domain Setup',
      dnsStatus: 'Pending DNS',
      sslStatus: 'SSL Pending',
      hostingConnected: 'None',
      emailEnabled: false,
      renewalDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    };
    setDomains([...domains, newDom]);
    onAddLog('NexoraX Domains', 'Connect Domain requested', 'SUCCESS', `Queued raw handshake configuration records for domain: "${name}"`);
    alert(`Domain Connection Success!\n\nPlease point your domain registrar to NexoraX nameservers:\n\nns1.nexorax.co.uk\nns2.nexorax.co.uk\n\nDNS changes can take time to update globally.`);
    setDomainSearchOutput(null);
    setDomainSearchQuery('');
  };

  // Create Business Mailbox
  const handleCreateMailbox = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMailUser.trim()) return;
    const fullEmail = `${newMailUser.toLowerCase()}@${newMailDomain}`;
    if (mailboxes.find(m => m.email === fullEmail)) {
      alert('This mail route exists on the ledger. Choose another mailbox prefix.');
      return;
    }

    const newBox = {
      id: `mail-${Date.now()}`,
      email: fullEmail,
      type: newMailType,
      status: 'Pending Setup',
      storage: '0 MB / 10 GB',
      forwarding: newMailType.includes('forwarding'),
      smtp: true,
      lastActivity: 'Never'
    };

    setMailboxes([...mailboxes, newBox]);
    onAddLog('NexoraX Mail', 'Create Business Mailbox', 'SUCCESS', `Created mail routing configuration: "${fullEmail}" [Type: ${newMailType}]`);
    alert(`Mailbox successfully configured: ${fullEmail}\n\nOur system has initialized the mailbox and mapped it to your workspace. Complete DNS and SPF verification to activate send/receive.`);
    setNewMailUser('');
  };

  // Generate AI reply draft (connected to Nexus Agent)
  const handleGenerateAiReply = () => {
    setIsDraftLoading(true);
    setTimeout(() => {
      let draft = '';
      if (selectedMail.id === 'msg-1') {
        draft = `To Eleanor Vance,\n\nThis is Lead Director, Director of NexoraX Digital. We received your request under NI reference NH448796B.\n\nI will initiate a direct communication call regarding your Principal Private Secretary interview options. Please keep your line 07359062464 clear today.\n\nRegards,\nInstitutional Dispatch Node-18`;
      } else if (selectedMail.id === 'msg-3') {
        draft = `To Secure Payment Provider Client Services Team,\n\nRegarding the £150,000 Resource resource allocations, our legal arbiters at Node-16 have authorized the signature protocols. Enclosed are our Secure Payment Provider route verifications.\n\nNexoraX Chief of Staff Node`;
      } else {
        draft = `Dear Client,\n\nIn response to your query regarding "${selectedMail.subject}", our system is generating your custom deployment layout on Hetzner Bare-Metal servers.\n\nOne of our operations personnel will call you back on the freephone frequency 08000 622827.\n\nRegards,\nEnterprise Support Hub`;
      }
      setAiDraftMessage(draft);
      setIsDraftLoading(false);
      onAddLog('NexoraX Mail', 'AI Draft reply generated', 'SUCCESS', `NexoraX AI Agent analyzed threat metrics and composed reply draft for: "${selectedMail.sender}"`);
    }, 1200);
  };

  // Add a task from the inbox straight to the WorkSuite PM
  const handleCreateWorkSuiteTask = () => {
    const taskTitle = `INBOX TRACE: "${selectedMail.subject.toUpperCase()}" FROM ${selectedMail.sender.toUpperCase()}`;
    onAddTaskToWorkSuite(taskTitle, selectedMail.urgency === 'Urgent' ? 'URGENT' : 'HIGH');
    onAddLog('NexoraX Mail', 'Escalate Mail to WorkSuite', 'SUCCESS', `Created high priority workspace coordinate: ${taskTitle}`);
    alert(`WorkSuite Task Generated Successful!\n\nMapped mail data from ${selectedMail.sender} to core operational checklists.`);
  };

  // Verify single DNS Record
  const handleVerifyDnsRecord = (id: string, recordType: string) => {
    setDnsRecords(prev => prev.map(rec => {
      if (rec.id === id) {
        return { ...rec, status: 'Verified' };
      }
      return rec;
    }));
    onAddLog('NexoraX DNS', 'Verify individual pointer', 'SUCCESS', `Verified DNS records type: ${recordType} target on Hetzner shunts.`);
  };

  // Refresh auth checklist statuses
  const handleRefreshAuthChecklist = () => {
    setAuthChecklist(prev => prev.map((item, idx) => {
      if (idx === 3 || idx === 6) {
        return { ...item, status: 'Verified' };
      }
      return item;
    }));
    onAddLog('NexoraX Mail', 'Manual auth verification sweep', 'SUCCESS', `Checked DKIM, DMARC, and unsubscribe headers status globally. Dynamic indicators updated.`);
    alert('Security check complete. DMARC policies are fully aligned now.');
  };

  // Template placeholders hydration
  useEffect(() => {
    if (selectedTemplate) {
      let hydrated = selectedTemplate.body
        .replace(/\[BUSINESS_NAME\]/g, 'NexoraX Digital Ltd')
        .replace(/\[CLIENT_NAME\]/g, user.displayName || 'Vanguard Representative')
        .replace(/\[WEBSITE_URL\]/g, domains[0]?.name || 'nexoraxdigital.co.uk')
        .replace(/\[SUPPORT_LINK\]/g, 'https://support.nexoraxdigital.co.uk/invoice/ns-7729')
        .replace(/\[BOOKING_LINK\]/g, 'https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00')
        .replace(/\[PLAN_NAME\]/g, `NexoraX ${user.plan} Account Tier`)
        .replace(/\[CTA\]/g, 'SECURE INTERFACE ACTIVATION');
      setPreviewTemplateReplaced(hydrated);
    }
  }, [selectedTemplate, user, domains]);

  return (
    <div className="w-full space-y-6">
      {/* GLOWING HERO BLOCK */}
      <div className="glass-panel border-2 border-purple-500/20 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-[#0c0f1d] via-[#05060d] to-black relative shadow-[0_0_50px_rgba(232,64,243,0.15)] overflow-hidden">
        {/* Background cosmic light */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-purple-600/5 blur-[120px] rounded-full pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-600/5 blur-[120px] rounded-full pointer-events-none"></div>

        <div className="md:flex md:items-center md:justify-between gap-6 relative z-10">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="p-2 bg-gradient-to-r from-purple-900 to-cyan-950 border border-purple-500/30 rounded-xl">
                <Mail className="w-6 h-6 text-purple-400 animate-pulse" />
              </span>
              <span className="text-amber-400 font-mono text-[10px] font-bold uppercase tracking-[0.3em] bg-amber-400/10 px-2 py-0.5 rounded border border-amber-400/20">
                NexoraX Add-On Portal
              </span>
            </div>
            <h1 className="text-2xl md:text-4xl font-sans font-black text-white uppercase tracking-tight">
              Nexus Mail & Domains
            </h1>
            <p className="text-sm font-sans font-medium text-[#00ffff] normal-case">
              Business Email, Domain Registry, and Secure Sending — Connected to Your Nexus Workspace
            </p>
            <p className="text-xs text-slate-400 max-w-3xl font-sans normal-case leading-relaxed">
              Set up professional email addresses, connect domains, verify sending, manage DNS, and power your business communication from one secure command center. Keep website, hosting status, and outbound templates aligned.
            </p>
          </div>

          <div className="mt-4 md:mt-0 bg-black/60 border border-purple-500/30 p-4 rounded-xl font-mono text-xs min-w-[220px] space-y-2 shrink-0">
            <div className="flex justify-between border-b border-zinc-900 pb-1.5 uppercase font-bold text-slate-500 text-[10px]">
              <span>Search Credit Pools</span>
              <Sliders size={11} className="text-cyan-400" />
            </div>
            <div className="flex justify-between text-white font-bold text-sm">
              <span>{searchCredits} Queries</span>
              <span className="text-[#00ffff] font-black">Active</span>
            </div>
            <button
              onClick={() => setActiveTab('Wizard')}
              className="w-full py-1.5 bg-[#e840f3] hover:opacity-90 text-black font-black uppercase text-[10px] rounded-lg tracking-wider font-mono cursor-pointer transition-all hover:scale-[1.02] flex items-center justify-center gap-1"
            >
              <Zap className="w-3.5 h-3.5 fill-black" />
              Launch Setup Wizard
            </button>
          </div>
        </div>

        {/* Tabs navigation */}
        <div className="mt-8 border-t border-zinc-900/60 pt-4 flex flex-wrap gap-2 text-xs font-mono font-black uppercase">
          {[
            { id: 'Overview', label: 'Overview HUD', icon: Sliders },
            { id: 'Domains', label: 'Domain Registry', icon: Globe },
            { id: 'Mailboxes', label: 'Business Mailboxes', icon: Mail },
            { id: 'AuthCenter', label: 'Auth Center', icon: Shield },
            { id: 'DNS', label: 'DNS Record Manager', icon: Database },
            { id: 'Inbox', label: 'Nexus Mailbox', icon: Inbox },
            { id: 'Smtp', label: 'SMTP & Sending', icon: Key },
            { id: 'Templates', label: 'AI Email Templates', icon: FileText },
            { id: 'Wizard', label: 'Setup Wizard', icon: Zap }
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id as any)}
                className={`py-2 px-3.5 rounded-xl cursor-pointer hover:bg-zinc-900/40 border transition-all flex items-center gap-1.5 ${
                  activeTab === t.id
                    ? 'bg-purple-950/40 text-[#e840f3] border-purple-500/40 shadow-[0_0_15px_rgba(232,64,243,0.15)]'
                    : 'text-slate-500 border-transparent hover:text-slate-300'
                }`}
              >
                <Icon size={13} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {/* VIEW 1: OVERVIEW HUD */}
        {activeTab === 'Overview' && (
          <motion.div
            key="mail-over"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {/* Top KPIs Cards Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Domains Connected', val: domains.filter(d => d.status === 'Connected').length, total: domains.length, desc: 'Active DNS Groundings', color: 'text-cyan-400 border-cyan-500/20' },
                { label: 'Mailboxes Active', val: mailboxes.filter(m => m.status === 'Active' || m.status === 'SMTP Ready').length, total: mailboxes.length, desc: 'NexoraX SMTP Endpoints', color: 'text-purple-400 border-purple-500/20' },
                { label: 'DNS Health Index', val: '99.9%', total: 'Secure', desc: 'Verify latency checks', color: 'text-emerald-400 border-emerald-500/20' },
                { label: 'Email Auth Status', val: '92%', total: 'Active', desc: 'Secure encryption badges', color: 'text-amber-400 border-amber-500/20' },
                { label: 'SSL Status', val: 'Active', total: 'TLS 1.3', desc: 'Nginx certificate proxy', color: 'text-cyan-450 border-cyan-500/10' },
                { label: 'Daily Sending Volume', val: '12,482', total: '50K Limit', desc: 'Relay throughput index', color: 'text-pink-400 border-pink-500/10' },
                { label: 'Support Inbox Inbound', val: inboxEmails.filter(i => i.status === 'New').length, total: inboxEmails.length, desc: 'Awaiting SLA compliance', color: 'text-rose-400 border-rose-500/10' },
                { label: 'Domain Renewal Status', val: 'Stable', total: 'No Due', desc: 'Master lease alignments', color: 'text-blue-400 border-blue-500/10' }
              ].map((kpi, idx) => (
                <div key={idx} className={`glass-panel border p-4 rounded-xl bg-black/40 space-y-1 ${kpi.color}`}>
                  <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{kpi.label}</div>
                  <div className="text-xl font-bold text-white font-sans tracking-tight flex items-baseline gap-1">
                    {kpi.val}
                    <span className="text-[10px] font-mono text-slate-500">/{kpi.total}</span>
                  </div>
                  <p className="text-[8.5px] font-mono text-slate-450 leading-none">{kpi.desc}</p>
                </div>
              ))}
            </div>

            {/* Dashboard Splitting panels */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column: SETUP PROGRESS WIDGET & DOMAIN STATUSES */}
              <div className="lg:col-span-2 space-y-6">
                {/* Visual Setup Process Tracker */}
                <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/35 space-y-4">
                  <div className="flex justify-between items-center border-b border-zinc-900 pb-3">
                    <h3 className="text-xs font-mono font-black text-white uppercase tracking-widest flex items-center gap-2">
                      <Zap className="text-amber-400 w-4 h-4" />
                      Nexus Domain & Mail Onboarding Index
                    </h3>
                    <span className="text-[10px] font-mono text-[#00ffff] bg-cyan-950/40 px-2 py-0.5 rounded border border-cyan-500/30">
                      STEP 4 OF 8 ACTIVE
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                    {[
                      { step: 1, name: 'Select Domain', active: true, done: true },
                      { step: 2, name: 'Nameserver Delegation', active: true, done: true },
                      { step: 3, name: 'Hetzner Hosting Link', active: true, done: true },
                      { step: 4, name: 'Setup Mailboxes', active: true, done: false },
                      { step: 5, name: 'Verify Authentications', active: false, done: false },
                      { step: 6, name: 'Cryptographic SPF/DKIM', active: false, done: false },
                      { step: 7, name: 'Relay Delivery Test', active: false, done: false },
                      { step: 8, name: 'Live Workspace Activation', active: false, done: false }
                    ].map((step) => (
                      <div
                        key={step.step}
                        onClick={() => {
                          setActiveTab('Wizard');
                          setCurrentWizardStep(step.step);
                        }}
                        className={`p-3.5 border rounded-xl text-center font-mono cursor-pointer transition-all ${
                          step.done ? 'bg-emerald-950/20 border-emerald-500/30 text-emerald-400' :
                          step.active ? 'bg-purple-950/20 border-purple-500/40 text-purple-300 shadow-[0_0_10px_rgba(232,64,243,0.1)]' :
                          'bg-black/40 border-zinc-900 text-slate-500 hover:border-zinc-800'
                        }`}
                      >
                        <div className="text-[10px] font-bold">STEP 0{step.step}</div>
                        <div className="text-[9px] uppercase tracking-wide truncate pt-1 leading-tight font-medium">{step.name}</div>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 bg-[#e840f3]/5 border-l-2 border-l-[#e840f3] border border-transparent rounded-r-xl flex items-start gap-3">
                    <Sparkles className="w-4 h-4 text-[#e840f3] mt-0.5 shrink-0" />
                    <p className="text-xs text-slate-300 leading-normal normal-case font-medium">
                      <strong className="text-white uppercase font-bold text-[10px] block mb-0.5">NexoraX AI Agent Setup Assistant Directive:</strong>
                      Domain &lt;galaxyquantum.app&gt; is awaiting record verification on Cloudflare edge proxies. Point your registrars to <span className="text-cyan-400">ns1.nexorax.co.uk</span> to automatically inherit SSL/TLS transport triggers and spawn custom info@ and support@ business handles instantly.
                    </p>
                  </div>
                </div>

                {/* Connected Domains and mailboxes status lists */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Active domains checklist */}
                  <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/30 space-y-3 font-mono text-xs">
                    <h4 className="text-white font-black text-xs uppercase tracking-wide border-b border-zinc-900 pb-2 flex justify-between items-center">
                      <span>Domain Registry Status</span>
                      <Globe className="w-3.5 h-3.5 text-cyan-400" />
                    </h4>
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      {domains.map(dom => (
                        <div key={dom.id} className="p-2 bg-black/50 border border-zinc-900 rounded-lg flex justify-between items-center text-[10px]">
                          <div>
                            <span className="text-white font-bold">{dom.name}</span>
                            <span className="text-slate-500 block text-[9px]">{dom.sslStatus} • {dom.hostingConnected}</span>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase ${
                            dom.status === 'Connected' ? 'bg-emerald-950/40 border border-emerald-500/30 text-emerald-400' :
                            'bg-amber-950/40 border border-amber-500/30 text-amber-500'
                          }`}>
                            {dom.status}
                          </span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => setActiveTab('Domains')}
                      className="w-full text-center py-2 bg-zinc-900 hover:bg-zinc-850 border border-zinc-800 hover:border-zinc-755 text-white font-mono text-[9px] uppercase tracking-widest rounded-lg transition-all"
                    >
                      [ Manage Domain Nodes ]
                    </button>
                  </div>

                  {/* Mailbox quick directory */}
                  <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/30 space-y-3 font-mono text-xs">
                    <h4 className="text-white font-black text-xs uppercase tracking-wide border-b border-zinc-900 pb-2 flex justify-between items-center">
                      <span>Active Handles Status</span>
                      <Mail className="w-3.5 h-3.5 text-purple-400" />
                    </h4>
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      {mailboxes.map(mail => (
                        <div key={mail.id} className="p-2 bg-black/50 border border-zinc-900 rounded-lg flex justify-between items-center text-[10px]">
                          <div className="truncate max-w-[140px]">
                            <span className="text-white font-bold">{mail.email}</span>
                            <span className="text-slate-500 block text-[9px]">{mail.type} • {mail.storage}</span>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase ${
                            mail.status === 'Active' ? 'bg-emerald-950/40 border border-emerald-500/30 text-emerald-400' :
                            mail.status === 'SMTP Ready' ? 'bg-cyan-950/40 border border-cyan-500/30 text-cyan-400' :
                            'bg-amber-950/40 border border-amber-500/30 text-amber-500'
                          }`}>
                            {mail.status}
                          </span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => setActiveTab('Mailboxes')}
                      className="w-full text-center py-2 bg-zinc-900 hover:bg-zinc-850 border border-zinc-800 hover:border-zinc-755 text-white font-mono text-[9px] uppercase tracking-widest rounded-lg transition-all"
                    >
                      [ Manage Mailbox Handles ]
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Column: AGENT DISPATCH AND SECURE DIRECTIVES */}
              <div className="space-y-6">
                {/* Inbox Activity feed with Agent assist options */}
                <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/30 space-y-4 font-mono text-xs text-zinc-400">
                  <h3 className="text-white font-black text-xs uppercase tracking-wide border-b border-zinc-900 pb-2 flex items-center justify-between">
                    <span>Inbox Telemetry Pulse</span>
                    <Inbox className="w-3.5 h-3.5 text-purple-400" />
                  </h3>

                  <div className="space-y-3">
                    {inboxEmails.slice(0, 3).map(email => (
                      <div
                        key={email.id}
                        onClick={() => {
                          setSelectedMail(email);
                          setActiveTab('Inbox');
                        }}
                        className="p-2.5 bg-black/60 border border-zinc-900 hover:border-purple-500/30 rounded-xl cursor-pointer transition-all space-y-1 group"
                      >
                        <div className="flex justify-between items-center text-[9px]">
                          <span className="text-[#00ffff]/80 group-hover:text-[#00ffff] font-bold">{email.sender}</span>
                          <span className="text-slate-500 text-[8px]">{email.date}</span>
                        </div>
                        <h4 className="text-white font-bold tracking-tight text-[10px] truncate uppercase">{email.subject}</h4>
                        <p className="text-[9.5px] text-slate-500 line-clamp-1">{email.body}</p>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => setActiveTab('Inbox')}
                    className="w-full text-center py-2.5 bg-purple-950/30 hover:bg-purple-900/20 border border-purple-500/30 text-[#e840f3] font-mono text-[10px] uppercase tracking-widest rounded-lg transition-all cursor-pointer"
                  >
                    [ Enter Dedicated Mailbox ]
                  </button>
                </div>

                {/* Secure Payment details note under safe labels */}
                <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-gradient-to-tr from-[#02030b] to-black space-y-3 font-mono text-[10px] text-slate-500">
                  <h4 className="text-amber-400 font-bold uppercase text-xs tracking-wider flex items-center gap-1">
                    <Shield className="w-3.5 h-3.5" />
                    NexoraX Escrow & Payments
                  </h4>
                  <p className="leading-relaxed normal-case font-medium"> All billing, search credit purchases, and registered domain setup requests are handled via the Secure Payment Provider gateway. Manual bank transfers can be aligned via the custom Invoice dispatch forms securely.</p>
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-900/60">
                    <span className="bg-cyan-950/30 text-cyan-400 border border-cyan-800/30 px-2 py-0.5 rounded text-[8px]">
                      Secure Checkout Enabled
                    </span>
                    <span className="bg-emerald-950/30 text-emerald-400 border border-emerald-800/30 px-2 py-0.5 rounded text-[8px]">
                      DKIM/SPF Audited
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* VIEW 2: DOMAIN REGISTRY & CONNECTION */}
        {activeTab === 'Domains' && (
          <motion.div
            key="domains-tab"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            {/* Domain Availability search bar */}
            <div className="glass-panel border border-[#00ffff]/20 p-6 rounded-2xl bg-black/40 relative">
              <h3 className="text-sm font-mono font-black text-white uppercase tracking-widest border-b border-zinc-900 pb-3 mb-4 flex items-center gap-2">
                <Globe className="text-cyan-400 w-4 h-4" />
                NexoraX Domain Registration & Setup Checks
              </h3>

              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  placeholder="Query domain availability (e.g. brandnexus.co.uk)..."
                  value={domainSearchQuery}
                  onChange={(e) => setDomainSearchQuery(e.target.value)}
                  className="flex-1 bg-black/60 border border-zinc-850 rounded-xl py-3 px-4 text-xs font-mono text-white tracking-wider outline-none focus:border-[#00ffff]/50 transition-colors uppercase"
                />
                <button
                  onClick={handleCheckDomain}
                  className="px-6 py-3 bg-[#0a2540] hover:bg-cyan-950 text-[#00ffff] border border-cyan-500/20 font-mono text-xs font-black uppercase rounded-xl transition-all cursor-pointer flex items-center gap-1 text-center justify-center"
                >
                  Check Availability
                </button>
              </div>

              {domainSearchOutput && (
                <div className="mt-4 p-4 border border-zinc-850 bg-black/50 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 font-mono text-xs">
                  <div>
                    <span className="text-slate-500 uppercase text-[9px] block">Search Result for:</span>
                    <span className="text-white font-bold text-sm uppercase">{domainSearchOutput.name}</span>
                    <p className={`text-[10px] uppercase mt-1 font-bold ${domainSearchOutput.available ? 'text-emerald-400' : 'text-rose-450'}`}>
                      {domainSearchOutput.available ? '✓ AVAILABLE FOR REGISTRATION PLACEHOLDER' : '✖ DOMAIN ALREADY SECURED'}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    {domainSearchOutput.available ? (
                      <>
                        <span className="text-emerald-400 font-bold text-sm">£{domainSearchOutput.price?.toFixed(2)}/yr</span>
                        <button
                          onClick={() => handleConnectDomain(domainSearchOutput.name)}
                          className="px-4 py-2 bg-gradient-to-r from-purple-650 to-cyan-600 text-black font-mono text-xs font-black uppercase rounded-lg tracking-wider transition-all hover:scale-[1.02] cursor-pointer"
                        >
                          Request Domain Setup
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleConnectDomain(domainSearchOutput.name)}
                        className="px-4 py-2 bg-zinc-900 hover:bg-zinc-850 text-[#00ffff] border border-cyan-500/20 font-mono text-[10px] font-black uppercase rounded-lg cursor-pointer"
                      >
                        Connect Existing Domain
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-4 text-[9.5px] font-mono text-slate-500 flex flex-wrap gap-x-6 gap-y-1 uppercase">
                <span>* Safe Label: Registrar API Required</span>
                <span>* Domain Registration Available Soon</span>
                <span>* Point DNS to inherit SSL automatically</span>
              </div>
            </div>

            {/* Domains Table list */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Left Column: Connection Instructions */}
              <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/30 font-sans text-xs space-y-4">
                <h4 className="text-white font-mono font-black text-xs uppercase tracking-wider border-b border-zinc-900 pb-2">
                  DNS Connection Guidelines
                </h4>
                <p className="text-slate-400 leading-relaxed normal-case font-medium">
                  To bind any external domain to your secure Hetzner node or email server relays, you must replace your current DNS nameservers at your domain registrar.
                </p>

                <div className="space-y-2.5 font-mono text-[9.5px]">
                  <div className="p-3 bg-black/60 border border-zinc-900 rounded-xl space-y-1.5 relative group">
                    <span className="text-slate-500">PRIMARY NAMESERVER:</span>
                    <div className="flex justify-between items-center text-white">
                      <span>ns1.nexorax.co.uk</span>
                      <button
                        onClick={() => handleCopy('ns1.nexorax.co.uk', 'ns1')}
                        className="text-[#00ffff] hover:underline hover:scale-105 transition-transform"
                      >
                        {lastCopiedText === 'ns1' ? 'Copied' : <Copy size={11} />}
                      </button>
                    </div>
                  </div>

                  <div className="p-3 bg-black/60 border border-zinc-900 rounded-xl space-y-1.5 relative group">
                    <span className="text-slate-500">SECONDARY NAMESERVER:</span>
                    <div className="flex justify-between items-center text-white">
                      <span>ns2.nexorax.co.uk</span>
                      <button
                        onClick={() => handleCopy('ns2.nexorax.co.uk', 'ns2')}
                        className="text-[#00ffff] hover:underline hover:scale-105 transition-transform"
                      >
                        {lastCopiedText === 'ns2' ? 'Copied' : <Copy size={11} />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-purple-950/15 border border-purple-500/20 rounded-xl">
                  <span className="text-white uppercase font-bold font-mono text-[10px] block mb-1">SSL Certificate Info:</span>
                  <p className="text-slate-400 leading-relaxed text-[11px] normal-case">
                    Our servers handle continuous Let's Encrypt automated challenge cycles. Pointing nameservers will activate high-encryption TLS 1.3 buffers in under 1 hour.
                  </p>
                </div>
              </div>

              {/* Right Columns: Connected Domains list */}
              <div className="md:col-span-2 glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/30 space-y-4">
                <h4 className="text-white font-mono font-black text-xs uppercase tracking-wider border-b border-zinc-900 pb-2">
                  Active Connected Domain registry
                </h4>

                <div className="space-y-3 font-mono text-xs">
                  {domains.map((dom) => (
                    <div
                      key={dom.id}
                      className="p-4 border border-zinc-900 hover:border-purple-500/30 rounded-xl bg-black/50 hover:bg-black/80 transition-all duration-300 relative overflow-hidden flex flex-col md:flex-row md:items-center justify-between gap-4"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h5 className="text-white font-bold text-sm uppercase">{dom.name}</h5>
                          <span className={`px-2 py-0.5 rounded text-[8.5px] font-black uppercase ${
                            dom.status === 'Connected' ? 'bg-emerald-950/40 text-emerald-400 border border-emerald-500/30' :
                            dom.status === 'Pending DNS' ? 'bg-amber-950/40 text-amber-500 border border-amber-500/30' :
                            'bg-rose-955/40 text-rose-400 border border-rose-500/30'
                          }`}>
                            {dom.status}
                          </span>
                        </div>
                        <p className="text-slate-500 text-[10px] uppercase">
                          Registrar: <strong className="text-zinc-400">{dom.registrar}</strong> • Renewal: {dom.renewalDate}
                        </p>
                        <div className="flex flex-wrap gap-2 text-[8.5px] uppercase pt-1">
                          <span className="bg-zinc-900 text-slate-400 px-1.5 py-0.2 rounded">SSL: {dom.sslStatus}</span>
                          <span className="bg-zinc-900 text-slate-400 px-1.5 py-0.2 rounded">Hosting: {dom.hostingConnected}</span>
                          <span className="bg-zinc-900 text-slate-400 px-1.5 py-0.2 rounded">DNS: {dom.dnsStatus}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setActiveTab('DNS');
                            onAddLog('NexoraX DNS', 'Check domain registry pointer parameters', 'SUCCESS', `Entered pointer settings for: ${dom.name}`);
                          }}
                          className="px-3.5 py-2 bg-zinc-900 hover:bg-zinc-850 hover:text-white border border-zinc-800 text-slate-400 rounded-lg text-[10px] font-black uppercase tracking-wider cursor-pointer"
                        >
                          Setup DNS
                        </button>
                        <button
                          onClick={() => {
                            const confirmRenew = window.confirm(`SECURE RENEWAL REQUEST\n\nExtend registry period for domain ${dom.name} by 1 year?\nCost: £12.00 Recurrent\n\nSubmit checkout?`);
                            if (confirmRenew) {
                              onAddLog('NexoraX Billing', 'Renew domain asset tenure', 'SUCCESS', `Tenure extended for domain: ${dom.name}`);
                              alert('SUCCESS! Renewal invoice dispatched.');
                            }
                          }}
                          className="px-3.5 py-2 bg-[#e840f3]/10 hover:bg-[#e840f3]/25 border border-[#e840f3]/30 text-white rounded-lg text-[10px] font-black uppercase tracking-wider cursor-pointer"
                        >
                          Renew Lease
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* VIEW 3: BUSINESS EMAIL SETUP */}
        {activeTab === 'Mailboxes' && (
          <motion.div
            key="mailboxes-tab"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            {/* Create mailbox form */}
            <div className="glass-panel border border-zinc-850 p-6 rounded-2xl bg-black/40 relative">
              <h3 className="text-sm font-mono font-black text-white uppercase tracking-widest border-b border-zinc-900 pb-3 mb-4 flex items-center gap-2">
                <Mail className="text-purple-400 w-4 h-4" />
                Initialize Professional Business Email Mailbox
              </h3>

              <form onSubmit={handleCreateMailbox} className="grid grid-cols-1 md:grid-cols-4 gap-4 font-mono text-xs">
                <div className="space-y-1.5">
                  <label className="text-slate-500 uppercase font-black text-[9px] block">Username Prefix</label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      placeholder="e.g. info, support, hello"
                      value={newMailUser}
                      onChange={(e) => setNewMailUser(e.target.value)}
                      className="w-full bg-black/60 border border-zinc-850 rounded-xl py-2.5 px-3 whitespace-nowrap text-white text-xs outline-none focus:border-purple-400"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-slate-500 uppercase font-black text-[9px] block">Bound Domain</label>
                  <select
                    value={newMailDomain}
                    onChange={(e) => setNewMailDomain(e.target.value)}
                    className="w-full bg-black/60 border border-zinc-850 rounded-xl py-2.5 px-3 text-white text-xs outline-none focus:border-purple-400 uppercase"
                  >
                    {domains.map(d => (
                      <option key={d.id} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-slate-500 uppercase font-black text-[9px] block">Mailbox Delivery Type</label>
                  <select
                    value={newMailType}
                    onChange={(e) => setNewMailType(e.target.value)}
                    className="w-full bg-black/60 border border-zinc-850 rounded-xl py-2.5 px-3 text-white text-xs outline-none focus:border-purple-400"
                  >
                    <option value="Business mailbox">Business mailbox (Dedicated Cloud Storage)</option>
                    <option value="Forwarding address">Forwarding address (Sinks inbound)</option>
                    <option value="Shared inbox">Shared inbox (B2B Multi-node)</option>
                    <option value="Support inbox">Support inbox (Auto-Sla checks)</option>
                    <option value="Sales inbox">Sales inbox (Lead Research linked)</option>
                    <option value="Billing inbox">Billing inbox (Tax auditing)</option>
                    <option value="No-reply sender">No-reply sender (Alert dispatches)</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-[#e840f3] hover:opacity-90 text-black font-black uppercase text-xs rounded-xl tracking-wider cursor-pointer shadow-[0_0_15px_rgba(232,64,243,0.2)]"
                  >
                    Set Up Mailbox Handle
                  </button>
                </div>
              </form>

              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-[9.5px] font-mono text-slate-500 uppercase border-t border-zinc-900/60 pt-3">
                <span className="text-[#e840f3] font-bold">Recommended Handles:</span>
                <span>info@</span>
                <span>support@</span>
                <span>sales@</span>
                <span>admin@</span>
                <span>hello@</span>
                <span>bookings@</span>
                <span>billing@</span>
                <span>or any custom handle prefix</span>
              </div>
            </div>

            {/* Mailboxes list table */}
            <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/30 space-y-4">
              <h4 className="text-white font-mono font-black text-xs uppercase tracking-wider border-b border-zinc-900 pb-2">
                Active Mailbox & Forwarding Directory
              </h4>

              <div className="space-y-3 font-mono text-xs">
                {mailboxes.map((mail) => (
                  <div
                    key={mail.id}
                    className="p-4 border border-zinc-900 hover:border-cyan-500/20 rounded-xl bg-black/50 hover:bg-black/80 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="p-1 px-2 rounded bg-zinc-950 border border-zinc-900 text-white font-bold text-xs">
                          {mail.email}
                        </span>
                        <span className="text-[10px] text-slate-500 uppercase">[{mail.type}]</span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-[9.5px] text-slate-400 pt-1.5 uppercase">
                        <span>Status: <strong className={`${mail.status === 'Active' ? 'text-emerald-450' : 'text-cyan-450'}`}>{mail.status}</strong></span>
                        <span>Hosting Storage: <strong className="text-zinc-300">{mail.storage}</strong></span>
                        <span>SMTP Status: <strong className="text-zinc-300">{mail.smtp ? 'Enabled' : 'Disabled'}</strong></span>
                        <span>Last Trace activity: <strong className="text-zinc-300">{mail.lastActivity}</strong></span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => {
                          const routeTo = window.prompt(`SET FORWARDING ROUTE\n\nEnter recipient email details to forward traffic from ${mail.email} automatically:`, 'forward-target@nexorax.co.uk');
                          if (routeTo) {
                            onAddLog('NexoraX Mail', 'Create Forwarding route', 'SUCCESS', `Routed ${mail.email} inbound traffic automatically to ${routeTo}`);
                            alert(`Forwarding configured!\n\nAll traffic received on ${mail.email} will now silently clone logs to: ${routeTo}`);
                          }
                        }}
                        className="py-1.5 px-3 bg-zinc-900 hover:bg-zinc-850 text-slate-400 hover:text-white border border-zinc-800 rounded-lg text-[9px] uppercase tracking-wider cursor-pointer"
                      >
                        Forwarding Rules
                      </button>
                      <button
                        onClick={() => {
                          const answer = window.confirm(`DELETE MAILBOX METADATA\n\nAre you sure you want to completely wipe the mailbox configuration for ${mail.email}?\nThis is non-reversible.`);
                          if (answer) {
                            setMailboxes(mailboxes.filter(m => m.id !== mail.id));
                            onAddLog('NexoraX Mail', 'Wipe Mailbox handle', 'WARNING', `Completely truncated email mailbox: ${mail.email}`);
                          }
                        }}
                        className="p-1.5 bg-rose-950/20 hover:bg-rose-900/40 border border-rose-500/30 text-rose-400 rounded-lg cursor-pointer"
                        title="Delete Handle"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* VIEW 4: EMAIL AUTHORIZATION CENTER */}
        {activeTab === 'AuthCenter' && (
          <motion.div
            key="auth-center-tab"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            {/* Header / Purpose info */}
            <div className="glass-panel border border-zinc-850 p-6 rounded-2xl bg-black/40 space-y-4">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 border-b border-zinc-900 pb-3">
                <div>
                  <h3 className="text-sm font-mono font-black text-white uppercase tracking-widest flex items-center gap-2">
                    <Shield className="text-cyan-400 w-4 h-4 animate-pulse" />
                    NexoraX Send Integrity & Deliverability Center
                  </h3>
                  <p className="text-xs text-slate-400 mt-1 max-w-2xl leading-relaxed normal-case font-medium">
                    Verify sending reputation, authorize SPF/DKIM key handshakes, and track DMARC bounce loops. Properly verified emails bypass spam filters and land securely in user inboxes.
                  </p>
                </div>

                <button
                  onClick={handleRefreshAuthChecklist}
                  className="px-4 py-2 bg-purple-950/40 hover:bg-purple-900/20 border border-purple-500/30 text-[#e840f3] rounded-lg font-mono text-xs font-black uppercase tracking-wider cursor-pointer flex items-center gap-1 shrink-0"
                >
                  <RefreshCw size={12} />
                  Force Verifications Sweep
                </button>
              </div>

              {/* Checklist details */}
              <div className="space-y-3 font-mono text-xs">
                {authChecklist.map((check) => (
                  <div key={check.id} className="p-3.5 border border-zinc-900 rounded-xl bg-[#020409] flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2.5">
                        <span className={`w-2 h-2 rounded-full ${
                          check.status === 'Verified' ? 'bg-emerald-400' :
                          check.status === 'Pending DNS' ? 'bg-amber-400 animate-pulse' :
                          'bg-rose-400'
                        }`}></span>
                        <h4 className="text-white font-bold uppercase tracking-tight">{check.name}</h4>
                      </div>
                      <p className="text-slate-500 text-[10.5px] leading-relaxed normal-case font-medium">{check.details}</p>
                    </div>

                    <span className={`px-2.5 py-0.5 rounded text-[8.5px] font-black uppercase select-none shrink-0 text-center ${
                      check.status === 'Verified' ? 'bg-emerald-950/40 border border-emerald-500/30 text-emerald-400' :
                      check.status === 'Pending DNS' ? 'bg-amber-950/40 border border-amber-500/30 text-amber-500' :
                      check.status === 'Needs Review' ? 'bg-rose-955/20 border border-rose-500/30 text-rose-400' :
                      'bg-zinc-900 border border-zinc-800 text-slate-400'
                    }`}>
                      {check.status}
                    </span>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-black/60 border border-zinc-900 rounded-xl text-[9.5px] font-mono text-slate-500 space-y-1.5">
                <div className="text-zinc-400 font-bold uppercase">🔐 PLATFORM SECURITY AUDITING PROTOCOLS</div>
                <p className="normal-case leading-relaxed">
                  In compliance with security regulations, actual SMTP credentials, workspace private keys, API tokens, and provider webhook secrets are stored strictly server-side inside secure environment scopes. Only DNS verification records are exposed to clients for registrars updates.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* VIEW 5: DNS RECORD MANAGER */}
        {activeTab === 'DNS' && (
          <motion.div
            key="dns-tab"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            {/* Live DNS table */}
            <div className="glass-panel border border-zinc-850 p-6 rounded-2xl bg-black/40 space-y-4">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 border-b border-zinc-900 pb-3">
                <div>
                  <h3 className="text-sm font-mono font-black text-white uppercase tracking-widest flex items-center gap-2">
                    <Database className="text-cyan-400 w-4 h-4 animate-pulse" />
                    Secure DNS Record Configuration Hub
                  </h3>
                  <p className="text-xs text-slate-400 mt-1 max-w-2xl leading-relaxed normal-case font-medium font-sans">
                    Configure nameserver routing pointers, MX configurations, domain TXT verifications, and mail transfer keys. DNS changes can take up to 24-48 hours to update globally due to proxy caching.
                  </p>
                </div>

                <button
                  onClick={() => {
                    onAddLog('NexoraX DNS', 'Request DNS Setup Help', 'PENDING_APPROVAL', 'Assigned support coordinate to assist client with DNS.');
                    alert('NEXORAX DIRECT DESK INTEGRATION:\n\nDNS Support Request Queued!\nOur network engineers will assist you over the 08000 622827 communications frequency.');
                  }}
                  className="px-4 py-2 bg-[#0c1a30] hover:bg-cyan-950 text-[#00ffff] border border-cyan-500/20 rounded-lg font-mono text-xs font-black uppercase tracking-wider cursor-pointer flex items-center gap-1.5 shrink-0"
                >
                  <HelpCircle size={13} />
                  Request DNS Setup Help
                </button>
              </div>

              {/* DNS Warn */}
              <div className="p-4 bg-amber-400/5 border border-amber-400/20 rounded-xl flex items-start gap-3 text-xs text-amber-300 normal-case">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <p className="leading-relaxed font-medium">
                  <strong>Critical Security Notice:</strong> Direct DNS modifiers can disrupt company web-views and hosting routing. Editing is limited to Administrator override layers. Please verify values exactly before altering pointer indexes.
                </p>
              </div>

              {/* Records Matrix */}
              <div className="space-y-3 font-mono text-[10px]">
                {dnsRecords.map((rec) => (
                  <div key={rec.id} className="p-4 border border-zinc-900/60 hover:border-cyan-500/10 rounded-xl bg-black/60 hover:bg-black/80 transition-all flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="bg-zinc-900 text-[#00ffff] font-extrabold px-1.5 py-0.5 rounded border border-zinc-850">{rec.type}</span>
                        <span className="text-slate-400 font-bold">Host:</span>
                        <strong className="text-white bg-black/40 px-1 py-0.2 rounded font-mono">{rec.host}</strong>
                        <span className="text-slate-500 font-bold">Required For:</span>
                        <span className="text-slate-400 text-[9px] uppercase font-bold italic">{rec.requiredFor}</span>
                      </div>
                      <div className="flex justify-between items-center text-slate-300 bg-[#020509] p-2 rounded-lg border border-zinc-950 mt-1 select-all break-all">
                        <span>{rec.value}</span>
                        <button
                          onClick={() => handleCopy(rec.value, rec.id)}
                          className="text-cyan-400 hover:text-white ml-2 transition-colors duration-200"
                        >
                          {lastCopiedText === rec.id ? 'Copied!' : <Copy size={11} />}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`px-2 py-0.5 rounded text-[8.5px] font-black uppercase ${
                        rec.status === 'Verified' ? 'bg-emerald-950/40 text-emerald-400 border border-emerald-500/20' :
                        'bg-amber-950/40 text-amber-500 border border-amber-500/25 animate-pulse'
                      }`}>
                        {rec.status}
                      </span>
                      {rec.status !== 'Verified' && (
                        <button
                          onClick={() => handleVerifyDnsRecord(rec.id, rec.type)}
                          className="py-1.5 px-3 bg-cyan-950/40 hover:bg-cyan-500/20 text-[#00ffff] border border-cyan-500/30 rounded-lg text-[9px] uppercase tracking-wider font-bold transition-all cursor-pointer"
                        >
                          Verify Record
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* VIEW 6: EMAIL INBOX (Nexus Mailbox) */}
        {activeTab === 'Inbox' && (
          <motion.div
            key="inbox-tab"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Inbox list (1/3 col) */}
              <div className="glass-panel border border-zinc-850 p-4 rounded-2xl bg-black/40 space-y-4 max-h-[640px] overflow-y-auto">
                <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
                  <h4 className="text-white font-mono font-black text-xs uppercase tracking-wider flex items-center gap-1.5">
                    <Inbox className="w-4 h-4 text-purple-400" />
                    Nexus Mailbox list
                  </h4>
                  <span className="text-[9.5px] font-mono text-purple-450 uppercase tracking-widest">{inboxEmails.length} messages</span>
                </div>

                <div className="space-y-3">
                  {inboxEmails.map(mail => (
                    <div
                      key={mail.id}
                      onClick={() => setSelectedMail(mail)}
                      className={`p-3 border rounded-xl cursor-pointer transition-all space-y-1.5 relative overflow-hidden ${
                        selectedMail.id === mail.id
                        ? 'bg-purple-950/20 border-purple-500/40 shadow-[0_0_10px_rgba(232,64,243,0.1)]'
                        : 'bg-black/50 border-zinc-900 hover:border-zinc-800'
                      }`}
                    >
                      {mail.status === 'New' && (
                        <div className="absolute top-0 right-0 w-2 h-2 bg-[#e840f3] rounded-bl-lg"></div>
                      )}

                      <div className="flex justify-between items-center font-mono text-[9px]">
                        <span className="text-[#00ffff] font-extrabold truncate max-w-[125px]">{mail.sender}</span>
                        <span className="text-slate-500">{mail.date}</span>
                      </div>
                      <h4 className="text-white text-[10px] font-sans font-extrabold uppercase tracking-tight leading-tight line-clamp-1">{mail.subject}</h4>
                      <div className="flex justify-between items-center text-[8px] uppercase pt-1">
                        <span className="text-slate-500">{mail.category}</span>
                        <span className={`px-1.5 rounded font-black ${
                          mail.urgency === 'Urgent' ? 'bg-rose-955 text-rose-455 animate-pulse' :
                          mail.urgency === 'High' ? 'bg-amber-955 text-amber-500' : 'bg-zinc-800 text-slate-400'
                        }`}>{mail.urgency}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Message Details & AI Assist (2/3 col) */}
              <div className="md:col-span-2 glass-panel border border-[#00ffff]/10 p-5 rounded-2xl bg-black/35 flex flex-col justify-between min-h-[500px]">
                {selectedMail ? (
                  <div className="space-y-6">
                    {/* Message Header */}
                    <div className="border-b border-zinc-900 pb-4 space-y-3 font-mono">
                      <div className="flex flex-wrap justify-between items-start gap-2">
                        <div className="space-y-1">
                          <span className="text-slate-500 text-[9px] uppercase block">FROM:</span>
                          <strong className="text-white text-xs">{selectedMail.sender}</strong>
                        </div>
                        <div className="text-right">
                          <span className="text-slate-500 text-[9px] uppercase block">RECEIVED ON:</span>
                          <span className="text-slate-400 text-xs">{selectedMail.date}</span>
                        </div>
                      </div>

                      <div className="space-y-1 pt-1">
                        <span className="text-slate-500 text-[9px] uppercase block">SUBJECT:</span>
                        <h4 className="text-white text-sm font-sans font-black uppercase tracking-tight leading-normal">{selectedMail.subject}</h4>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-2 text-[9px] uppercase">
                        <span className="bg-zinc-950 text-slate-400 border border-zinc-900 px-2.5 py-0.5 rounded">Inbox: {selectedMail.category}</span>
                        <span className="bg-zinc-950 text-slate-400 border border-zinc-900 px-2.5 py-0.5 rounded">Urgency: {selectedMail.urgency}</span>
                      </div>
                    </div>

                    {/* Email Message Content Body */}
                    <div className="p-4 bg-zinc-950/60 border border-zinc-900 rounded-xl font-sans text-xs text-slate-300 leading-relaxed whitespace-pre-wrap select-text normal-case max-h-72 overflow-y-auto">
                      {selectedMail.body}
                    </div>

                    {/* AI Agent Summarizer & Reply drafter */}
                    <div className="p-4 bg-purple-950/10 border border-purple-500/20 rounded-xl space-y-4 font-mono text-xs text-zinc-400">
                      <div className="flex justify-between items-center border-b border-purple-900/40 pb-2">
                        <span className="text-white uppercase font-black text-[10px] flex items-center gap-1.5">
                          <Cpu className="text-[#e840f3] w-4 h-4 animate-pulse" />
                          Nexus Agent Copilot Assist
                        </span>
                        <span className="text-[8.5px] font-mono text-[#e840f3] uppercase bg-[#e840f3]/10 border border-[#e840f3]/30 px-2 py-0.2 rounded">
                          0.8s Telemetry Core
                        </span>
                      </div>

                      <div className="space-y-2">
                        <span className="text-[#e840f3] font-bold text-[9px] block">AI STRUCTURAL SUMMARY:</span>
                        <p className="text-slate-300 leading-normal text-[11px] normal-case bg-black/40 p-2.5 rounded-lg border border-zinc-900">
                          {selectedMail.aiSummary}
                        </p>
                      </div>

                      {aiDraftMessage ? (
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-[#e840f3] font-bold text-[9px]">AI GENERATED REPLY CO-ORDINATE DRAFT:</span>
                            <span className="text-[8.5px] text-amber-500 uppercase font-black">Requires Human Approval to route externally</span>
                          </div>
                          <textarea
                            value={aiDraftMessage}
                            onChange={(e) => setAiDraftMessage(e.target.value)}
                            className="w-full bg-[#030612] border border-[#e840f3]/30 rounded-xl p-3 text-xs text-slate-300 font-sans tracking-tight focus:outline-none focus:border-[#e840f3] normal-case"
                            rows={5}
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => {
                                onAddLog('NexoraX Mail', 'Send AI response approved', 'SUCCESS', `Dispatched approved email template reply to ${selectedMail.sender}`);
                                alert(`SUCCESS!\n\nApproved mail dispatch routed to edge SMTP relays. Address: ${selectedMail.sender}`);
                                setAiDraftMessage('');
                              }}
                              className="py-1.5 px-3.5 bg-gradient-to-r from-purple-650 to-[#e840f3] text-black font-black uppercase text-[10px] rounded-lg tracking-wider transition-all cursor-pointer shadow-[0_0_10px_rgba(232,64,243,0.15)] flex items-center gap-1"
                            >
                              <Send size={11} className="fill-black" />
                              Approve & Dispatch Reply
                            </button>
                            <button
                              onClick={() => setAiDraftMessage('')}
                              className="py-1.5 px-3.5 bg-zinc-950 border border-zinc-900 text-slate-500 hover:text-white rounded-lg text-[10px] uppercase cursor-pointer"
                            >
                              Abandone Draft
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={handleGenerateAiReply}
                            disabled={isDraftLoading}
                            className="py-2 px-4 bg-purple-950/40 hover:bg-[#e840f3]/10 border border-[#e840f3]/30 text-[#e840f3] rounded-lg text-[10px] uppercase font-black tracking-wider transition-all cursor-pointer flex items-center gap-1.5"
                          >
                            <Sparkles size={12} className={isDraftLoading ? 'animate-spin text-purple-400' : 'text-purple-400'} />
                            {isDraftLoading ? 'Reading Cognitive indices...' : 'Draft AI Reply'}
                          </button>

                          <button
                            onClick={handleCreateWorkSuiteTask}
                            className="py-2 px-4 bg-cyan-950/40 hover:bg-cyan-900/10 border border-cyan-500/20 text-cyan-400 rounded-lg text-[10px] uppercase font-black tracking-wider transition-all cursor-pointer flex items-center gap-1.5"
                          >
                            <Plus size={12} />
                            Escalate to WorkSuite Task
                          </button>

                          <button
                            onClick={() => {
                              setInboxEmails(prev => prev.map(m => m.id === selectedMail.id ? { ...m, status: 'Resolved' } : m));
                              onAddLog('NexoraX Mail', 'Mark task resolved', 'SUCCESS', `Archived support ticket regarding: ${selectedMail.subject}`);
                              alert('Ticket marked as Resolved.');
                            }}
                            className="py-2 px-4 bg-emerald-950/40 hover:bg-emerald-900/10 border border-emerald-500/20 text-emerald-400 rounded-lg text-[10px] uppercase font-black tracking-wider transition-all cursor-pointer flex items-center gap-1.5 ml-auto"
                          >
                            <Check size={12} />
                            Mark Resolved
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-20 font-mono text-xs text-slate-500">
                    <Inbox className="w-8 h-8 text-slate-650 animate-pulse mb-3" />
                    <span>Select an inbox email to manage or trigger AI drafts</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* VIEW 7: SMTP & SENDING SETTINGS */}
        {activeTab === 'Smtp' && (
          <motion.div
            key="smtp-tab"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 font-mono text-xs">
              {/* Clients secure sending view */}
              <div className="glass-panel border border-zinc-850 p-6 rounded-2xl bg-black/40 space-y-4">
                <h3 className="text-sm font-black text-white uppercase tracking-widest border-b border-zinc-900 pb-2 flex items-center gap-1.5">
                  <Key className="text-purple-400 w-4 h-4" />
                  SMTP Relay & Deliverability Settings
                </h3>

                <div className="space-y-3 pt-2">
                  <div className="flex justify-between border-b border-zinc-900/40 pb-2">
                    <span className="text-slate-500 uppercase text-[9px]">Outgoing SMTP Relay:</span>
                    <span className="text-white font-bold">{smtpStatusInfo.providerConnected}</span>
                  </div>
                  <div className="flex justify-between border-b border-zinc-900/40 pb-2">
                    <span className="text-slate-500 uppercase text-[9px]">Verified Sending Domain:</span>
                    <span className="text-white font-bold">{smtpStatusInfo.domainVerified}</span>
                  </div>
                  <div className="flex justify-between border-b border-zinc-900/40 pb-2">
                    <span className="text-slate-500 uppercase text-[9px]">Daily Ingress SMTP Limit:</span>
                    <span className="text-[#e840f3] font-bold">{smtpStatusInfo.dailySendingLimit}</span>
                  </div>
                  <div className="flex justify-between border-b border-zinc-900/40 pb-2">
                    <span className="text-slate-500 uppercase text-[9px]">Monthly Ingress Limit balance:</span>
                    <span className="text-[#00ffff] font-extrabold">{smtpStatusInfo.monthlySendingLimit}</span>
                  </div>
                  <div className="flex justify-between border-b border-zinc-900/40 pb-2">
                    <span className="text-slate-500 uppercase text-[9px]">Bounce Handler status:</span>
                    <span className="text-emerald-400 font-bold">{smtpStatusInfo.bounceHandlingStatus}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 uppercase text-[9px]">Last Relay Diagnostic Check:</span>
                    <span className="text-white font-bold">{smtpStatusInfo.lastTestStatus}</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-zinc-900 flex justify-end">
                  <button
                    onClick={() => {
                      onAddLog('NexoraX SMTP', 'Relate SMTP Delivery check test', 'SUCCESS', 'Triggered test email relay. Sequence successfully validated.');
                      setSmtpStatusInfo(prev => ({ ...prev, lastTestStatus: `Success (${new Date().toLocaleTimeString()} UTC)` }));
                      alert('SMTP Test Sequence Complete!\n\nDispatched test dispatch to support@nexoraxdigital.co.uk. Header verifications passed with perfect DKIM checksums.');
                    }}
                    className="py-2 px-4 bg-gradient-to-r from-purple-650 to-cyan-600 font-mono text-xs font-black uppercase text-black rounded-lg tracking-wider hover:opacity-90 transition-all cursor-pointer"
                  >
                    Send Deliverability Test E-mail
                  </button>
                </div>
              </div>

              {/* Admin configuration status overlay panel */}
              <div className="glass-panel border border-[#00ffff]/20 p-6 rounded-2xl bg-black/60 space-y-4">
                <div className="h-full flex flex-col justify-between">
                  <div className="space-y-4">
                    <h3 className="text-sm font-black text-white uppercase tracking-widest border-b border-[#00ffff]/15 pb-2 flex items-center justify-between">
                      <span>Server-Side Relay API Status (Admin-Only)</span>
                      <Sliders size={13} className="text-cyan-400 animate-pulse" />
                    </h3>

                    <div className="space-y-3 font-mono text-[9.5px]">
                      {[
                        { label: 'Ingress Relay Module', val: 'Active (Hetzner Dedicated)', secure: true },
                        { label: 'Mail Server Port Bindings', val: 'Port 587 TLS Secure Access', secure: true },
                        { label: 'Relay Host Encryption Key', val: 'API Key Stored Server-Side', secure: true },
                        { label: 'SPF Certificate Validation webhook', val: 'Webhook Configured & Active', secure: true },
                        { label: 'SPF Bounce/Denial callback', val: 'Bounce webhook verified target', secure: true },
                        { label: 'Mailbox Allocation Quota Check', val: 'Quota Verified OK', secure: true }
                      ].map((item, idx) => (
                        <div key={idx} className="p-2.5 bg-[#030612] border border-zinc-900 rounded-lg flex justify-between items-center">
                          <span className="text-slate-500 uppercase">{item.label}:</span>
                          <span className="text-white font-bold">{item.val}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <p className="text-[9.5px] text-slate-500 leading-normal normal-case border-t border-zinc-900/60 pt-3 mt-4">
                    <strong>Admin Notice:</strong> Relays execute strictly over SSL secure links. Actual SMTP passwords, private tokens, and registrar master keys are isolated. No secret keys or tokens are cached on browser client states.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* VIEW 8: EMAIL TEMPLATES */}
        {activeTab === 'Templates' && (
          <motion.div
            key="templates-tab"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 font-mono text-xs">
              {/* Templates Selector Library (1/3 col) */}
              <div className="glass-panel border border-zinc-850 p-4 rounded-2xl bg-black/40 space-y-4 max-h-[580px] overflow-y-auto">
                <div className="border-b border-zinc-900 pb-2">
                  <h4 className="text-white font-black text-xs uppercase tracking-wide flex items-center gap-1.5">
                    <FileText className="w-4 h-4 text-cyan-400" />
                    NexoraX Templates
                  </h4>
                </div>

                <div className="space-y-2.5">
                  {templates.map(tpl => (
                    <div
                      key={tpl.id}
                      onClick={() => setSelectedTemplate(tpl)}
                      className={`p-3 border rounded-xl cursor-pointer transition-all space-y-1 ${
                        selectedTemplate.id === tpl.id
                        ? 'bg-purple-950/20 border-purple-500/40 shadow-[0_0_10px_rgba(232,64,243,0.1)]'
                        : 'bg-black/50 border-zinc-900 hover:border-zinc-800'
                      }`}
                    >
                      <h4 className="text-white font-bold text-[10.5px] uppercase tracking-tight leading-none truncate">{tpl.name}</h4>
                      <p className="text-[9px] text-zinc-500">{tpl.category}</p>
                      {tpl.approvalRequired && (
                        <span className="text-[7.5px] font-black uppercase text-amber-500 bg-amber-500/10 px-1.5 rounded inline-block mt-1">
                          Requires Approval to route
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Template Editor Previewer (2/3 col) */}
              <div className="md:col-span-2 glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/35 flex flex-col justify-between min-h-[480px]">
                {selectedTemplate ? (
                  <div className="space-y-6">
                    <div className="border-b border-zinc-900 pb-3 space-y-1.5">
                      <span className="text-slate-500 text-[9px] uppercase block">TEMPLATE CATEGORY:</span>
                      <div className="flex justify-between items-center">
                        <strong className="text-[#e840f3] font-black text-xs uppercase">{selectedTemplate.category}</strong>
                        <span className="text-[9px] text-slate-500">SUBJECT LINE PREVIEW:</span>
                      </div>
                      <h4 className="text-white font-sans font-black text-sm uppercase tracking-tight">{selectedTemplate.subject}</h4>
                    </div>

                    <div className="space-y-4">
                      <div className="flex justify-between text-[9px] text-slate-500 uppercase">
                        <span>Hydrated Preview With Auto-Placeholders:</span>
                        <span className="text-cyan-400 font-bold">* API values parsed</span>
                      </div>

                      <div className="p-4 bg-zinc-950/80 border border-zinc-900 rounded-xl font-sans text-xs text-slate-300 whitespace-pre-wrap select-all leading-relaxed normal-case max-h-60 overflow-y-auto">
                        {previewTemplateReplaced}
                      </div>

                      {/* Display Safe placeholders block */}
                      <div className="p-3 bg-black/60 border border-zinc-900 rounded-xl space-y-2">
                        <span className="text-slate-500 text-[8px] uppercase font-bold block border-b border-zinc-900/50 pb-1">Hydrated Variable Placeholders Log (Client Safe):</span>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[8px] uppercase">
                          <div>
                            <span className="text-slate-600 block">Name:</span>
                            <span className="text-white font-bold">[BUSINESS_NAME]</span>
                          </div>
                          <div>
                            <span className="text-slate-600 block">Client:</span>
                            <span className="text-white font-bold">[CLIENT_NAME]</span>
                          </div>
                          <div>
                            <span className="text-slate-600 block">Website:</span>
                            <span className="text-white font-bold">[WEBSITE_URL]</span>
                          </div>
                          <div>
                            <span className="text-slate-600 block">Ticket/Link:</span>
                            <span className="text-white font-bold">[SUPPORT_LINK]</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-zinc-900/60 flex flex-wrap gap-2 justify-end">
                      <button
                        onClick={() => {
                          handleCopy(previewTemplateReplaced, 'bodytext');
                          alert('Template source payload copied to clipboard!');
                        }}
                        className="py-1.5 px-3.5 bg-zinc-900 hover:bg-zinc-850 hover:text-white border border-zinc-850 rounded-lg text-[10px] font-black uppercase tracking-wider cursor-pointer"
                      >
                        Copy Template Payload
                      </button>
                      <button
                        onClick={() => {
                          const routeTo = window.prompt(`TEST DISPATCH\n\nEnter recipient email to send test dispatch of template "${selectedTemplate.name}":`, user.email);
                          if (routeTo) {
                            onAddLog('NexoraX SMTP', 'Test Dispatch Dispatcher', 'SUCCESS', `Dispatched template test "${selectedTemplate.name}" to ${routeTo}`);
                            alert(`Test dispatch successfully dispatched through secure relays!\nRecipient: ${routeTo}`);
                          }
                        }}
                        className="py-1.5 px-3.5 bg-[#e840f3]/10 hover:bg-[#e840f3]/25 border border-[#e840f3]/30 text-white rounded-lg text-[10px] font-black uppercase tracking-wider cursor-pointer"
                      >
                        Send Test Dispatch
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-20 text-slate-500">
                    <span>Select an email template layout to Hydrate or Edit</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* VIEW 9: WIZARD STEPPER */}
        {activeTab === 'Wizard' && (
          <motion.div
            key="wizard-tab"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            <div className="glass-panel border-2 border-purple-500/20 p-6 md:p-8 rounded-2xl bg-[#0c0f1d] relative shadow-[0_0_35px_rgba(232,64,243,0.1)]">
              {/* Steps indicators */}
              <div className="flex justify-between items-center border-b border-zinc-900 pb-5 mb-6 text-center text-[10px] font-mono select-none overflow-x-auto min-w-[300px]">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((step) => (
                  <div key={step} className="flex-1 min-w-[60px]">
                    <div className={`w-6 h-6 mx-auto rounded-full border-2 flex items-center justify-center font-bold mb-1 ${
                      currentWizardStep === step
                        ? 'border-[#e840f3] bg-[#e840f3] text-black shadow-[0_0_10px_rgba(232,64,243,0.5)]'
                        : currentWizardStep > step
                          ? 'border-emerald-500 bg-emerald-950/80 text-emerald-400'
                          : 'border-zinc-800 text-slate-500 bg-black'
                    }`}>
                      {step}
                    </div>
                    <span className="text-[8px] uppercase tracking-wide block leading-none pt-0.5 truncate max-w-[80px] mx-auto text-slate-450">
                      Step {step}
                    </span>
                  </div>
                ))}
              </div>

              {/* Steps views cases */}
              <div className="py-2 min-h-[220px]">
                {currentWizardStep === 1 && (
                  <div className="space-y-4 font-mono text-xs">
                    <h4 className="text-white font-sans font-black text-sm uppercase tracking-tight">Step 1: Choose or Register Your Workspace Domain Name</h4>
                    <p className="text-slate-400 leading-relaxed normal-case font-medium">To route email and host pages, choose your enterprise domain handle prefix:</p>
                    <div className="max-w-md space-y-3">
                      <input
                        type="text"
                        value={wizardDomainInput}
                        onChange={(e) => setWizardDomainInput(e.target.value)}
                        className="w-full bg-black/60 border border-zinc-850 rounded-xl py-2.5 px-3 text-xs text-white uppercase outline-none focus:border-purple-400"
                        placeholder="newventure-nexus.co.uk"
                      />
                      <button
                        onClick={() => {
                          onAddLog('NexoraX Wizard', 'Wizard Step 1 completed', 'SUCCESS', `Domain choice allocated: ${wizardDomainInput}`);
                          setCurrentWizardStep(2);
                        }}
                        className="py-2.5 px-5 bg-[#e840f3] hover:opacity-90 text-black font-black uppercase rounded-lg tracking-wider transition-all cursor-pointer inline-flex items-center gap-1"
                      >
                        Confirm Domain choice
                        <ArrowRight size={13} />
                      </button>
                    </div>
                  </div>
                )}

                {currentWizardStep === 2 && (
                  <div className="space-y-4 font-mono text-xs">
                    <h4 className="text-white font-sans font-black text-sm uppercase tracking-tight">Step 2: Connect or Request Domain registration</h4>
                    <p className="text-slate-400 leading-relaxed normal-case">Point your domain pointing pointers to our safe secure server grids.</p>
                    <div className="p-3 bg-black/60 border border-zinc-900 rounded-xl max-w-sm space-y-1">
                      <span className="text-slate-500 text-[8px]">PRIMARY NAMESERVER TARGET:</span>
                      <strong className="text-white block">ns1.nexorax.co.uk</strong>
                    </div>
                    <button
                      onClick={() => {
                        onAddLog('NexoraX Wizard', 'Wizard Nameservers aligned', 'SUCCESS', `Nameserver target confirmation done for: ${wizardDomainInput}`);
                        setCurrentWizardStep(3);
                      }}
                      className="py-2.5 px-5 bg-[#e840f3] hover:opacity-90 text-black font-black uppercase rounded-lg tracking-wider transition-all cursor-pointer inline-flex items-center gap-1"
                    >
                      Verify Nameserver Pointer
                      <ArrowRight size={13} />
                    </button>
                  </div>
                )}

                {currentWizardStep === 3 && (
                  <div className="space-y-4 font-mono text-xs">
                    <h4 className="text-white font-sans font-black text-sm uppercase tracking-tight">Step 3: Connect Hetzner Server Container Hosting Status</h4>
                    <p className="text-slate-400 normal-case leading-relaxed font-sans font-medium">Binds your selected domain directly to our dedicated German bare-metal PX93 cluster proxies on Port 3000 ingress networks.</p>
                    <div className="p-4 bg-emerald-950/20 border border-emerald-500/30 rounded-xl max-w-md text-emerald-400 flex items-center gap-2">
                      <CheckCircle size={14} />
                      <span>Dedicated Container Handshake aligned successfully</span>
                    </div>
                    <button
                      onClick={() => {
                        onAddLog('NexoraX Wizard', 'Wizard Step 3 completed', 'SUCCESS', 'Binds domain proxy to Hetzner web root container.');
                        setCurrentWizardStep(4);
                      }}
                      className="py-2.5 px-5 bg-[#e840f3] hover:opacity-90 text-black font-black uppercase rounded-lg tracking-wider transition-all cursor-pointer inline-flex items-center gap-1"
                    >
                      Link Hosting Status
                      <ArrowRight size={13} />
                    </button>
                  </div>
                )}

                {currentWizardStep === 4 && (
                  <div className="space-y-4 font-mono text-xs">
                    <h4 className="text-white font-sans font-black text-sm uppercase tracking-tight">Step 4: Configure Professional Business Handles</h4>
                    <p className="text-slate-400 leading-normal normal-case leading-relaxed">Initialize info@, support@ or custom handles for: <strong className="text-white">{wizardDomainInput}</strong>.</p>
                    <div className="max-w-md p-4 bg-black/60 border border-zinc-900 rounded-xl space-y-3">
                      <div className="flex gap-2">
                        <span className="py-1 px-3 bg-zinc-950 border border-zinc-850 rounded text-purple-400 font-bold">info@{wizardDomainInput}</span>
                        <span className="py-1 px-3 bg-zinc-950 border border-zinc-850 rounded text-cyan-400 font-bold">support@{wizardDomainInput}</span>
                      </div>
                      <p className="text-slate-500 text-[10px] normal-case leading-relaxed font-sans font-medium">These accounts are queued for deployment. You can alter access or rules later.</p>
                    </div>
                    <button
                      onClick={() => {
                        onAddLog('NexoraX Wizard', 'Wizard Step 4 Handles created', 'SUCCESS', `Created handles info@ and support@ for domain: ${wizardDomainInput}`);
                        setCurrentWizardStep(5);
                      }}
                      className="py-2.5 px-5 bg-[#e840f3] hover:opacity-90 text-black font-black uppercase tracking-wider rounded-lg transition-all cursor-pointer inline-flex items-center gap-1"
                    >
                      Initialize Handles
                      <ArrowRight size={13} />
                    </button>
                  </div>
                )}

                {currentWizardStep === 5 && (
                  <div className="space-y-4 font-mono text-xs">
                    <h4 className="text-white font-sans font-black text-sm uppercase tracking-tight">Step 5: Verify DNS record Alignments</h4>
                    <p className="text-slate-400 normal-case leading-normal font-sans font-medium">Verify MX and CNAME indicators to allow secure receiving and web-views.</p>
                    <div className="p-3 bg-black/60 border border-zinc-900 rounded-xl text-slate-350 max-w-sm flex justify-between items-center">
                      <span>MX @ 10 relay.nexoraxdigital.co.uk.</span>
                      <span className="text-emerald-400 uppercase text-[9px] font-bold">Verified</span>
                    </div>
                    <button
                      onClick={() => {
                        onAddLog('NexoraX Wizard', 'Wizard Step 5 finished pointer verification', 'SUCCESS', 'MX and A-pointers verifications passed.');
                        setCurrentWizardStep(6);
                      }}
                      className="py-2.5 px-5 bg-[#e840f3] hover:opacity-90 text-black font-black uppercase rounded-lg tracking-wider transition-all cursor-pointer inline-flex items-center gap-1"
                    >
                      Perform DNS Verification
                      <ArrowRight size={13} />
                    </button>
                  </div>
                )}

                {currentWizardStep === 6 && (
                  <div className="space-y-4 font-mono text-xs">
                    <h4 className="text-white font-sans font-black text-sm uppercase tracking-tight">Step 6: Authorize Crypographic Authentications (SPF/DKIM/DMARC)</h4>
                    <p className="text-slate-400 normal-case leading-relaxed font-sans font-medium">Inject domain signature policies to completely secure deliveries and eliminate ingress bounce risks.</p>
                    <div className="p-4 bg-[#020509] border border-zinc-900 rounded-xl space-y-2 max-w-md">
                      <div className="flex justify-between items-center text-[10px] text-zinc-400">
                        <span>SPF POLICY:</span>
                        <span className="text-[#00ffff] font-extrabold uppercase">Aligned</span>
                      </div>
                      <div className="flex justify-between items-center text-[10px] text-zinc-400">
                        <span>DKIM KEY SIGN:</span>
                        <span className="text-[#00ffff] font-extrabold uppercase">Aligned</span>
                      </div>
                      <div className="flex justify-between items-center text-[10px] text-zinc-400">
                        <span>DMARC RULE STATUS:</span>
                        <span className="text-emerald-450 font-bold uppercase">Active Verified</span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        onAddLog('NexoraX Wizard', 'Wizard Authentications fully verified', 'SUCCESS', 'SPF, DKIM, and DMARC settings verified and passed.');
                        setCurrentWizardStep(7);
                      }}
                      className="py-2.5 px-5 bg-[#e840f3] hover:opacity-90 text-black font-black uppercase rounded-lg tracking-wider transition-all cursor-pointer inline-flex items-center gap-1"
                    >
                      Authorize Authentications
                      <ArrowRight size={13} />
                    </button>
                  </div>
                )}

                {currentWizardStep === 7 && (
                  <div className="space-y-4 font-mono text-xs">
                    <h4 className="text-white font-sans font-black text-sm uppercase tracking-tight">Step 7: Send Secure Deliverability Test E-mail</h4>
                    <p className="text-slate-400 normal-case leading-normal font-sans font-medium">We will dispatch a secure loop email to test and resolve server port speed checks.</p>
                    <div className="max-w-md">
                      {wizardSmtpTestSent ? (
                        <div className="p-4 bg-emerald-950/20 border border-emerald-500/30 rounded-xl text-emerald-400 space-y-2">
                          <p className="font-bold flex items-center gap-2">
                            <CheckCircle size={15} />
                            Loop sequence completed successfully!
                          </p>
                          <p className="text-[10px] hover:underline normal-case">Relay latency checked around 0.12s. Perfect deliverability score achieved.</p>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            onAddLog('NexoraX Wizard', 'SMTP test email sequence dispatch', 'SUCCESS', 'Dispatched Wizard deliverability checks block.');
                            setWizardSmtpTestSent(true);
                          }}
                          className="py-2.5 px-5 bg-gradient-to-r from-purple-650 to-cyan-600 text-black font-black uppercase rounded-lg tracking-wider cursor-pointer"
                        >
                          Trigger Smtp Relay Test Email
                        </button>
                      )}
                    </div>

                    <button
                      onClick={() => setCurrentWizardStep(8)}
                      disabled={!wizardSmtpTestSent}
                      className="py-2.5 px-5 bg-[#e840f3] disabled:opacity-40 text-black font-black uppercase rounded-lg tracking-wider transition-all cursor-pointer inline-flex items-center gap-1"
                    >
                      Proceed to Final step
                      <ArrowRight size={13} />
                    </button>
                  </div>
                )}

                {currentWizardStep === 8 && (
                  <div className="space-y-4 font-mono text-xs text-center p-6 bg-black/40 border border-[#00ffff]/20 rounded-2xl relative">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-40 bg-purple-600/10 blur-[80px] rounded-full pointer-events-none"></div>

                    <div className="relative z-10 space-y-4 max-w-md mx-auto">
                      <div className="w-12 h-12 bg-emerald-950/45 border-2 border-emerald-400 rounded-full flex items-center justify-center text-emerald-400 mx-auto animate-bounce mb-3">
                        <Check size={24} />
                      </div>
                      <h4 className="text-white font-sans font-black text-xl uppercase tracking-tighter">Your Workspace Setup is Complete!</h4>
                      <p className="text-slate-300 normal-case leading-relaxed font-sans font-medium text-xs">
                        Your domain, hosting container, and business email setups are fully verified and ready for active client work tasks inside the NexoraX Nexus OS environment.
                      </p>

                      <div className="pt-2">
                        <button
                          onClick={() => {
                            // Add newly configured wizard domain to the domains state list
                            if (!domains.find(d => d.name === wizardDomainInput)) {
                              setDomains([...domains, {
                                id: `dom-wiz`,
                                name: wizardDomainInput,
                                status: 'Connected',
                                registrar: 'Registrar API Required',
                                dnsStatus: 'Connected',
                                sslStatus: 'SSL Active',
                                hostingConnected: 'Hetzner Dedicated PX93',
                                emailEnabled: true,
                                renewalDate: '2027-06-14'
                              }]);
                            }
                            onAddLog('NexoraX Wizard', 'Activated dashboard via wizard completes', 'SUCCESS', `Active domain verified and added via onboarding: ${wizardDomainInput}`);
                            setActiveTab('Overview');
                            setCurrentWizardStep(1);
                            setWizardSmtpTestSent(false);
                            alert('SUCCESS!\n\nYour onboarding setups are fully applied to your Mail & Domains control registry panel.');
                          }}
                          className="py-3 px-6 bg-gradient-to-r from-purple-600 to-[#00ffff] text-black font-black uppercase text-xs rounded-xl tracking-wider hover:opacity-90 active:scale-97 transition-all cursor-pointer shadow-[0_0_20px_rgba(0,255,255,0.3)]"
                        >
                          Activate Mail & Domains Dashboard
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
